import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  CircularProgress,
  Alert,
} from "@mui/material";
import autoTable from "jspdf-autotable";
import {
  jsPDF,
  houseTableDefaults,
  brandAccentLabel,
  drawLetterhead,
  drawFooter,
  LIGHT_GREY,
} from "../shared utils/pdfHouseStyle";
import { getCommissionStatement } from "./api/agentsApi";

function todayStr() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${day}-${m}-${d.getFullYear()}`;
}

const money = (n) => (n == null ? "—" : `£${Number(n).toFixed(2)}`);

// Red = student still owes, green = fully paid (£0); null/undefined stays plain.
const balanceBg = (o) => (o === true ? "#ffe6e6" : o === false ? "#e6ffe6" : undefined);
const balanceTitle = (o) =>
  o === true ? "Student has an outstanding balance" : o === false ? "Fully paid" : undefined;
const slug = (s) => String(s || "").trim().replace(/\s+/g, "-").replace(/[^\w-]/g, "") || "x";

const rateText = (commission, rate) => {
  const t = String(commission || "NONE").toUpperCase();
  if (t === "NONE") return "—";
  return `${t} ${rate != null ? `${rate}%` : ""}`.trim();
};

// Per-agent commission statement: a date-ordered ledger interleaving student
// commission-due lines and payment lines, with a running balance and totals.
export default function StatementDialog({ open, onClose, agent }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (range) => {
      if (!agent?.id) return;
      setLoading(true);
      setError(null);
      try {
        const res = await getCommissionStatement(agent.id, range || {});
        setData(res);
      } catch (e) {
        setError(e);
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [agent]
  );

  useEffect(() => {
    if (open) {
      setFrom("");
      setTo("");
      load({});
    } else {
      setData(null);
    }
  }, [open, load]);

  // Running balance: due adds, paid subtracts (ends at the reported balance).
  const rows = useMemo(() => {
    const lines = Array.isArray(data?.lines) ? data.lines : [];
    let bal = 0;
    return lines.map((l) => {
      if (l.type === "PAYMENT") bal -= Number(l.paymentAmount || 0);
      else bal += Number(l.commissionPayable || 0);
      return { ...l, runningBalance: bal };
    });
  }, [data]);

  const downloadPdf = () => {
    if (!data) return;
    const pdf = new jsPDF("portrait", "pt", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();

    let y = drawLetterhead(pdf, { title: "Statement" });

    // Agent name + contact details in the header.
    pdf.setFontSize(11);
    brandAccentLabel(pdf, "Agent", agent?.name || `Agent #${agent?.id}`, 40, y);
    brandAccentLabel(pdf, "Date", todayStr(), pageWidth - 220, y);
    y += 18;

    const contact = [agent?.email, agent?.phone].filter(Boolean).join("    ·    ");
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(40);
    if (contact) {
      pdf.text(contact, 40, y);
      y += 16;
    }

    const hasRange = from || to;
    if (hasRange) {
      pdf.text(`Period: ${from || "…"} to ${to || "…"}`, 40, y);
      y += 16;
    }
    y += 6;

    autoTable(pdf, {
      ...houseTableDefaults,
      startY: y,
      styles: { ...houseTableDefaults.styles, fontSize: 7, cellPadding: 3 },
      footStyles: { fillColor: LIGHT_GREY, textColor: 40, fontStyle: "bold" },
      // Portrait A4: keep numeric columns narrow so Detail takes the slack + wraps.
      columnStyles: {
        0: { cellWidth: 48 },
        1: { cellWidth: "auto" },
        2: { cellWidth: 54, halign: "right" },
        3: { cellWidth: 54, halign: "right" },
        4: { cellWidth: 40, halign: "right" },
        5: { cellWidth: 58, halign: "right" },
        6: { cellWidth: 46, halign: "right" },
        7: { cellWidth: 52, halign: "right" },
      },
      head: [["Date", "Detail", "Commissionable", "Non-comm.", "Rate", "Commission due", "Paid", "Balance"]],
      body: rows.map((l) =>
        l.type === "PAYMENT"
          ? [l.date, `Payment · ${l.paymentAccount}`, "—", "—", "—", "—", money(l.paymentAmount), money(l.runningBalance)]
          : [
              l.date,
              `${l.studentName} (${l.arrivalDate} – ${l.departureDate}${l.campus ? `, ${l.campus}` : ""})`,
              money(l.commissionableTotal),
              money(l.nonCommissionableTotal),
              rateText(l.commission, l.commissionRate),
              money(l.commissionPayable),
              "—",
              money(l.runningBalance),
            ]
      ),
      foot: [["", "Totals", "", "", "", money(data.totalCommissionPayable), money(data.totalPaid), money(data.balance)]],
      // Tint delinquent student rows red, matching the on-screen statement.
      didParseCell: (h) => {
        if (h.section !== "body") return;
        const line = rows[h.row.index];
        if (!line || line.type === "PAYMENT") return;
        // Red = owes, green = fully paid (£0); leave unknown plain.
        if (line.outstandingBalance === true) h.cell.styles.fillColor = [255, 230, 230];
        else if (line.outstandingBalance === false) h.cell.styles.fillColor = [230, 255, 230];
      },
    });

    drawFooter(pdf);
    pdf.save(`agent-${slug(agent?.name)}-commission-statement.pdf`);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Commission statement — {agent?.name || `Agent #${agent?.id}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <TextField
              label="From"
              type="date"
              size="small"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => load({ from: from || undefined, to: to || undefined })}
            >
              Apply
            </Button>
            {(from || to) && (
              <Button
                size="small"
                onClick={() => {
                  setFrom("");
                  setTo("");
                  load({});
                }}
              >
                Clear
              </Button>
            )}
          </Stack>

          {error ? <Alert severity="error">{String(error.message || error)}</Alert> : null}

          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2">Loading statement…</Typography>
            </Stack>
          ) : !data ? null : rows.length === 0 ? (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Nothing in this range.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Detail</TableCell>
                  <TableCell align="right">Commissionable</TableCell>
                  <TableCell align="right">Non-comm.</TableCell>
                  <TableCell>Rate</TableCell>
                  <TableCell align="right">Commission due</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((l, i) =>
                  l.type === "PAYMENT" ? (
                    <TableRow key={`p-${l.paymentId ?? i}`}>
                      <TableCell>{l.date}</TableCell>
                      <TableCell>Payment · {l.paymentAccount}</TableCell>
                      <TableCell align="right">—</TableCell>
                      <TableCell align="right">—</TableCell>
                      <TableCell>—</TableCell>
                      <TableCell align="right">—</TableCell>
                      <TableCell align="right">{money(l.paymentAmount)}</TableCell>
                      <TableCell align="right">{money(l.runningBalance)}</TableCell>
                    </TableRow>
                  ) : (
                    <TableRow
                      key={`s-${l.stayId ?? i}`}
                      sx={{ bgcolor: balanceBg(l.outstandingBalance) }}
                      title={balanceTitle(l.outstandingBalance)}
                    >
                      <TableCell>{l.date}</TableCell>
                      <TableCell>
                        {l.studentName} ({l.arrivalDate} – {l.departureDate}
                        {l.campus ? `, ${l.campus}` : ""})
                      </TableCell>
                      <TableCell align="right">{money(l.commissionableTotal)}</TableCell>
                      <TableCell align="right">{money(l.nonCommissionableTotal)}</TableCell>
                      <TableCell>{rateText(l.commission, l.commissionRate)}</TableCell>
                      <TableCell align="right">{money(l.commissionPayable)}</TableCell>
                      <TableCell align="right">—</TableCell>
                      <TableCell align="right">{money(l.runningBalance)}</TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={5} sx={{ fontWeight: 700 }}>
                    Totals
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {money(data.totalCommissionPayable)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {money(data.totalPaid)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {money(data.balance)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          )}

          {data ? (
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Balance = commission payable − paid. Red rows: the student has an outstanding
              balance — the agent may still claim commission. Green rows: the student is fully paid.
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={downloadPdf} disabled={!data || rows.length === 0}>
          Download PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
}
